import React, { useState, useEffect } from "react";
import axiosInstance from "../components/api/axiosInstance";
import "../assets/css/Projects.css";

const PAYMENT_TYPES = ["Advance", "Final", "Partial"];

function AddProjectPayment({ projectId, project, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    paymentDate: "",
    currencyId: "",
    amount: "",
    paymentModeId: "",
    transactionRef: "",
    totalAmount: "",
    paymentType: "Advance",
    remarks: ""
  });
  const [currencies, setCurrencies] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [loadingPaymentModes, setLoadingPaymentModes] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const projectIdNum = project?.Project_Id ?? project?.project_id ?? projectId;
  const customerIdNum = project?.client_id ?? project?.client_id;

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [currRes, modeRes] = await Promise.all([
          axiosInstance.get("/currencies").catch(() => ({ data: [] })),
          axiosInstance.get("/payment-modes").catch(() => ({ data: [] }))
        ]);

        const rawCurr = Array.isArray(currRes.data) ? currRes.data : currRes.data?.data ?? currRes.data?.currencies ?? [];
        const fallbackList = [
          { id: 1, name: "Indian Rupee", code: "INR", symbol: "₹" },
          { id: 2, name: "US Dollar", code: "USD", symbol: "$" }
        ];

        const currList = rawCurr.length > 0
          ? rawCurr.map((c) => ({
              id: c.Currency_Id ?? c.currency_id ?? c.id,
              name: c.Currency_Name ?? c.currency_name ?? c.name ?? "",
              code: c.Currency_Code ?? c.currency_code ?? c.code ?? "",
              symbol: c.Currency_Symbol ?? c.currency_symbol ?? c.symbol ?? ""
            }))
          : fallbackList;

        const filtered = currList.filter((c) => {
          const code = String(c?.code ?? "").trim().toUpperCase();
          const symbol = String(c?.symbol ?? "").trim();
          const isInr = code === "INR" || symbol === "₹";
          const isUsd = code === "USD" || (code === "" && symbol === "$");
          return isInr || isUsd;
        });

        setCurrencies(filtered.length > 0 ? filtered : fallbackList);

        const rawModes = Array.isArray(modeRes.data) ? modeRes.data : modeRes.data?.data ?? modeRes.data?.payment_modes ?? [];
        const modeList = rawModes.length > 0
          ? rawModes.map((m) => ({
              id: m.Payment_Mode_Id ?? m.payment_mode_id ?? m.id,
              name: m.Payment_Mode_Name ?? m.payment_mode_name ?? m.name ?? ""
            }))
          : [{ id: 1, name: "Cash" }, { id: 2, name: "Bank Transfer" }, { id: 3, name: "UPI" }, { id: 4, name: "Cheque" }];
        setPaymentModes(modeList);
      } catch (err) {
        console.error("Error fetching lookups:", err);
      } finally {
        setLoadingCurrencies(false);
        setLoadingPaymentModes(false);
      }
    };
    fetchLookups();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const getCreatedBy = () => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return 1;
      const user = JSON.parse(u);
      return user.user_id ?? user.id ?? 1;
    } catch {
      return 1;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectIdNum || !customerIdNum) {
      alert("Project and Customer are required.");
      return;
    }
    if (!formData.paymentDate?.trim() || !formData.amount?.trim()) {
      alert("Payment Date and Amount are required.");
      return;
    }
    const amountNum = parseFloat(String(formData.amount).replace(/,/g, ""));
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      alert("Please enter a valid amount.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        Project_Id: Number(projectIdNum),
        client_id: Number(customerIdNum),
        Payment_Date: formData.paymentDate,
        Currency_Id: formData.currencyId ? Number(formData.currencyId) : 1,
        Amount: amountNum,
        Payment_Mode_Id: formData.paymentModeId ? Number(formData.paymentModeId) : 1,
        Transaction_Ref: formData.transactionRef?.trim() || null,
        Total_Amount: formData.totalAmount ? parseFloat(String(formData.totalAmount).replace(/,/g, "")) : null,
        Payment_Type: formData.paymentType || "Advance",
        Remarks: formData.remarks?.trim() || null,
        Created_By: getCreatedBy()
      };

      const response = await axiosInstance.post("/create-project-payment", payload);
      const created = response?.data;
      alert("Payment added successfully.");
      if (onSuccess) onSuccess(created);
      setFormData({
        paymentDate: "",
        currencyId: "",
        amount: "",
        paymentModeId: "",
        transactionRef: "",
        totalAmount: "",
        paymentType: "Advance",
        remarks: ""
      });
    } catch (err) {
      console.error("Error creating project payment:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to add payment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="add-project-payment-form">
      <form onSubmit={handleSubmit} className="project-payment-form-grid">
        <div className="form-group">
          <label>Payment Date <span className="required">*</span></label>
          <input
            type="date"
            name="paymentDate"
            value={formData.paymentDate}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Currency</label>
          <select
            name="currencyId"
            value={formData.currencyId}
            onChange={handleChange}
            disabled={loadingCurrencies}
          >
            <option value="">{loadingCurrencies ? "Loading..." : "Select Currency"}</option>
            {currencies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.symbol || c.code || c.name || c.id}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Amount <span className="required">*</span></label>
          <input
            type="text"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Payment Mode</label>
          <select
            name="paymentModeId"
            value={formData.paymentModeId}
            onChange={handleChange}
            disabled={loadingPaymentModes}
          >
            <option value="">{loadingPaymentModes ? "Loading..." : "Select Mode"}</option>
            {paymentModes.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Transaction Ref</label>
          <input
            type="text"
            name="transactionRef"
            value={formData.transactionRef}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Total Amount</label>
          <input
            type="text"
            name="totalAmount"
            value={formData.totalAmount}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label>Payment Type</label>
          <select name="paymentType" value={formData.paymentType} onChange={handleChange}>
            {PAYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group form-group-full">
          <label>Remarks</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            rows={2}
          />
        </div>
        <div className="form-group form-group-actions">
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Adding..." : "Add Payment"}
          </button>
          {onCancel && (
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default AddProjectPayment;
